import { Link, useNavigate, useParams } from "@tanstack/react-router";

import { useArchiveRecipe, useRecipe } from "@/lib/api/recipes";
import { useNutrients } from "@/lib/api/nutrients";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/error-state";
import { AuditLogViewer } from "@/components/audit-log-viewer";

export function RecipeDetailPage() {
  const { recipeId } = useParams({
    from: "/chemistry-profile/recipes/$recipeId",
  });
  const navigate = useNavigate();
  const q = useRecipe(recipeId);
  const nutrients = useNutrients();
  const archive = useArchiveRecipe(recipeId);

  if (q.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.error) return <ErrorState error={q.error} retry={q.refetch} />;
  if (!q.data) return null;
  const r = q.data;
  const editable = !r.system_provided && !r.archived_at;

  const nutrientName = (id: string) =>
    nutrients.data?.items.find((n) => n.id === id)?.name ?? id.slice(-6);

  function onArchive() {
    const reason =
      window.prompt("Reason for archiving this recipe?") || "user archive";
    archive.mutate(reason, {
      onSuccess: () => navigate({ to: "/chemistry-profile/recipes" }),
    });
  }

  const entries = r.entries
    .slice()
    .sort((a, b) => a.order_rank - b.order_rank);

  return (
    <div className="space-y-6">
      <Link
        to="/chemistry-profile/recipes"
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ← Recipes
      </Link>

      <header className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {r.name}{" "}
            <span className="text-base font-normal text-muted-foreground">
              v{r.version}
            </span>
          </h1>
          <p className="text-sm text-muted-foreground">
            {r.description || "—"}
            {r.system_provided && " · system-provided"}
            {r.archived_at && " · archived"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {r.system_provided && (
            <span className="rounded-md border px-2.5 py-1 text-xs text-muted-foreground">
              Read-only · system-provided
            </span>
          )}
          {editable && (
            <>
              <Link
                to="/chemistry-profile/recipes/$recipeId/bump"
                params={{ recipeId: r.id }}
              >
                <Button variant="outline">Bump version</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={onArchive}
                disabled={archive.isPending}
              >
                {archive.isPending ? "Archiving…" : "Archive"}
              </Button>
            </>
          )}
          <Link to="/chemistry-profile/recipes/new" search={{ from: r.id }}>
            <Button variant="outline">Clone</Button>
          </Link>
        </div>
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Entries
        </h2>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No entries.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {entries.map((e, i) => (
              <li key={`${e.order_rank}-${e.nutrient_id}-${i}`} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs text-muted-foreground tabular-nums">
                      #{e.order_rank}
                    </span>
                    <Link
                      to="/chemistry-profile/nutrients/$nutrientId"
                      params={{ nutrientId: e.nutrient_id }}
                      className="font-medium hover:underline"
                    >
                      {nutrientName(e.nutrient_id)}
                    </Link>
                  </div>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {e.ml_per_gal} ml/gal
                  </span>
                </div>
                {e.gate_rules.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {e.gate_rules.map((g, gi) => (
                      <li
                        key={gi}
                        className="text-xs text-muted-foreground"
                      >
                        if <code>{g.if}</code> then <code>{g.then}</code>
                      </li>
                    ))}
                  </ul>
                )}
                {e.notes && (
                  <p className="mt-1 text-xs text-muted-foreground">{e.notes}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Audit trail
        </h2>
        <AuditLogViewer entityType="recipe" entityID={r.id} />
      </section>
    </div>
  );
}
